import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core';
import {
  faChartColumn,
  faMapLocationDot,
  faTableColumns,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faTable,
} from '@fortawesome/free-solid-svg-icons';
import MultiLineChart from '../graphs/MultiLineChart';
import MonthRangePicker from './MonthRangePicker';
import { apiFetch } from '../../api/apiClient';
import { usePortfolioDateFormatter } from '../../utils/usePortfolioDateFormatter';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

// ML_POWERBLOCK_BENCHMARK_MONTHLY.max_setpoint_mw — not inverter HI_LIM / P_FB.
const MAX_SERIES_CAPTION_SHORT =
  "Monthly max Power Plant Controller (PPC) solar power setpoint (MW DC) — the highest solar generation command sent to this power block's inverters each month.";

const fmtMwDc = (value, digits = 3) =>
  value != null && Number.isFinite(Number(value)) ? `${Number(value).toFixed(digits)} MW DC` : '--';

const fmtVariancePct = (value) =>
  value != null && Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : '--';

// Line color for the MAX chart.
const SERIES_COLORS = {
  max: '#9F0000',
};

// Stable references for the combined chart's Y-axis. Defined at module scope
// (not inline in JSX) so they don't change identity on every render, which
// would otherwise re-run MultiLineChart's draw effect each render and loop.
const COMBINED_DOMAIN = [0, 10];
const COMBINED_TICK_VALUES = [0, 2.5, 5, 7.5, 10];

// Reference lines on the combined chart: a dashed line at the 5 MW midpoint and
// dotted lines at the 7.5 / 2.5 MW marks. Module scope keeps the reference
// stable so it doesn't re-trigger the chart's draw effect each render.
const COMBINED_MIDLINES = [
  { value: 7.5, label: '', dash: '2,3' },
  { value: 5, label: '', dash: '8,4' },
  { value: 2.5, label: '', dash: '2,3' },
];

// Anchor colors for the 11-stop gradient band used to color powerblock tiles
// by their capacity difference: stop 0 = green, stop 5 = yellow, stop 10 = red.
const GRADIENT_ANCHORS = {
  green: '#008900',
  yellow: '#A48C00',
  red: '#9F0000',
};

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbToHex = (rgb) =>
  `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

const mixColors = (from, to, t) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * t));
};

// The 11 discrete band stops: green -> yellow across stops 0..5, then
// yellow -> red across stops 5..10.
const GRADIENT_STOPS = Array.from({ length: 11 }, (_, i) =>
  i <= 5
    ? mixColors(GRADIENT_ANCHORS.green, GRADIENT_ANCHORS.yellow, i / 5)
    : mixColors(GRADIENT_ANCHORS.yellow, GRADIENT_ANCHORS.red, (i - 5) / 5)
);

// Reference scale (MW) when all blocks are at peak; the live gradient max is
// derived from the largest difference in the current transformer view.
const MIN_DIFFERENCE_SCALE = 0.6;

// Map a difference value (0..scaleMax) to one of the 11 gradient stops.
const differenceToColor = (value, scaleMax = MIN_DIFFERENCE_SCALE) => {
  const max = scaleMax > 0 ? scaleMax : MIN_DIFFERENCE_SCALE;
  const clamped = Math.max(0, Math.min(max, value));
  const index = Math.round((clamped / max) * 10);
  return GRADIENT_STOPS[index];
};

const differenceToPercent = (value, scaleMax = MIN_DIFFERENCE_SCALE) => {
  const max = scaleMax > 0 ? scaleMax : MIN_DIFFERENCE_SCALE;
  return (1 - Math.max(0, Math.min(max, value)) / max) * 100;
};

const differenceToStatusIcon = (value, scaleMax = MIN_DIFFERENCE_SCALE) => {
  const percent = differenceToPercent(value, scaleMax);
  if (percent >= 90) return faCircleCheck;
  if (percent >= 50) return faCircleExclamation;
  return faCircleXmark;
};

const TRANSFORMER_IDS = ['1', '2', '3', '4'];

const TILE_LOADING_COLOR = '#d9d9d9';

/**
 * Parse Snowflake PERIOD_MONTH (first-of-month date, e.g. 2026-07-01 = July)
 * as a calendar-month anchor. Use UTC noon so plant-TZ formatting (US) and
 * d3 local month ticks do not shift the point back a day (UTC midnight → Jun 30).
 */
const parseBenchmarkMonthDate = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})/.exec(value.trim());
    if (match) {
      return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1, 12));
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 12));
};

/** Calendar YYYY-MM key. Series points use UTC noon month anchors; picker dates are local. */
const monthKey = (date, useUtc = false) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
  const month = (useUtc ? date.getUTCMonth() : date.getMonth()) + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
};

const normalizeBenchmarkMonthlyPayload = (raw) => {
  const rows = Array.isArray(raw) ? raw : [];
  return rows
    .map((row) => {
      const date = parseBenchmarkMonthDate(row.date ?? row.time ?? row.timestamp ?? row.t);
      const value = row.value ?? row.y ?? row.mwh;
      return { date, value: value != null ? Number(value) : NaN };
    })
    .filter((point) => point.date != null && !Number.isNaN(point.value));
};

// Keep only the `{ date, value }` points whose month falls within the selected
// range [start-of-start-month .. end-of-end-month]. A null/incomplete range
// passes everything through.
const filterPointsByRange = (points, range) => {
  if (!range?.start || !range?.end) return points;
  const startKey = monthKey(range.start, false);
  const endKey = monthKey(range.end, false);
  if (!startKey || !endKey) return points;
  return points.filter((point) => {
    const key = monthKey(point.date, true);
    return key != null && key >= startKey && key <= endKey;
  });
};

/** Value for a single calendar month (picker month ↔ UTC PERIOD_MONTH). */
const valueAtMonth = (points, monthDate) => {
  if (!monthDate) return null;
  const key = monthKey(monthDate, false);
  if (!key) return null;
  const point = (Array.isArray(points) ? points : []).find(
    (entry) => monthKey(entry.date, true) === key,
  );
  if (!point || !Number.isFinite(point.value)) return null;
  return point.value;
};

// Sort powerblock ids alphanumerically: letter prefix first, then numeric suffix
// (e.g. A1..A20, B1..B108, D1..D49, E1..E8, SB1).
const comparePowerblockIds = (a, b) => {
  const parse = (id) => {
    const match = String(id).match(/^([A-Za-z]*)(\d*)$/);
    return { prefix: match?.[1] ?? String(id), number: match?.[2] ? Number(match[2]) : 0 };
  };
  const pa = parse(a);
  const pb = parse(b);
  if (pa.prefix !== pb.prefix) {
    return pa.prefix.localeCompare(pb.prefix);
  }
  return pa.number - pb.number;
};

const getRangeMaxPoints = (points, range) =>
  filterPointsByRange(points, range)
    .filter((p) => p.date instanceof Date && !Number.isNaN(p.value))
    .sort((a, b) => a.date - b.date);

/* Unused Function 
const mostRecentMaxInRange = (points, range) => {
  const rangePoints = getRangeMaxPoints(points, range);
  if (rangePoints.length === 0) {
    return null;
  }
  for (let i = rangePoints.length - 1; i >= 0; i -= 1) {
    if (rangePoints[i].value > 0) {
      return rangePoints[i].value;
    }
  }
  return rangePoints[rangePoints.length - 1].value;
};
*/

const fmt = (value, digits = 3) => (value != null ? Number(value).toFixed(digits) : '--');

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function numericColumn(title, digits = 3) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }

      return fmt(cellData, digits);
    },
  };
}

function statusColumn(differenceToStatusIconFn, differenceToColorFn) {
  return {
    title: 'Status',
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }

      return '';
    },
    createdCell: (cell, cellData) => {
      const faIcon = differenceToStatusIconFn(cellData ?? 0);
      const color = differenceToColorFn(cellData ?? 0);
      const rendered = icon(faIcon);
      cell.innerHTML = rendered.html[0];
      const svg = cell.querySelector('svg');
      if (svg) {
        svg.style.color = color;
      }
    },
  };
}

function BenchmarkPowerBlocks({ portfolioName, onTooltip }) {
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const [t1xfmrView, setT1xfmrView] = useState(true);
  const [selectedXfmr, setSelectedXfmr] = useState('1');
  const [data, setData] = useState(null);
  const [activePB, setActivePB] = useState(0);
  const [activePBData, setActivePBData] = useState(null);
  const [allPBData, setAllPBData] = useState([]);
  const [pbBenchmarkById, setPbBenchmarkById] = useState({});
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);
  // Month range that windows the combined line chart / table.
  // Defaults to the last 6 months. Right-panel MIN/MAX/Difference use the
  // range end month only (see metricsMonthRange).
  const [chartRange, setChartRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 5, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 1),
    };
  });

  useEffect(() => {
    if (data && Object.keys(data).includes(activePB)) {
      setActivePBData(data[activePB]);
    } else {
      setActivePBData(null);
    }
  }, [data, activePB]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        let result;

        if (selectedXfmr === 'all') {
          const responses = await Promise.all(
            TRANSFORMER_IDS.map((id) =>
              apiFetch(`/api/metrics/portfolio/${portfolioName}/sector/powerblocks/${id}`).catch((apiError) => {
                console.warn(
                  `API fetch failed for /api/metrics/portfolio/${portfolioName}/sector/powerblocks/${id}:`,
                  apiError
                );
                return {};
              })
            )
          );
          const merged = responses.reduce(
            (acc, res) => (res && typeof res === 'object' && !res.error ? { ...acc, ...res } : acc),
            {}
          );
          result = Object.keys(merged)
            .sort(comparePowerblockIds)
            .reduce((acc, key) => {
              acc[key] = merged[key];
              return acc;
            }, {});
        } else {
          result = await apiFetch(`/api/metrics/portfolio/${portfolioName}/sector/powerblocks/${selectedXfmr}`);
        }

        if (!cancelled) {
          setData(result);
          setAllPBData(result);

          // set active PB to the first key (powerblock id) in object
          const keys = Object.keys(result);
          if (keys.length > 0) {
            setActivePB(keys[0]);
          }
        }
      } catch (apiError) {
        console.warn(
          `API fetch failed for /api/metrics/portfolio/${portfolioName}/sector/powerblocks (xfmr ${selectedXfmr}):`,
          apiError
        );
        if (!cancelled) {
          setData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedXfmr, portfolioName]);

  useEffect(() => {
    if (!data || typeof data !== 'object') {
      setPbBenchmarkById({});
      return undefined;
    }

    const ids = Object.keys(data);
    if (ids.length === 0) {
      setPbBenchmarkById({});
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const raw = await apiFetch(`/api/metrics/portfolio/${portfolioName}/powerblocks/${id}`);
            // API maps Snowflake MAX_SETPOINT_MW / MIN_SETPOINT_MW /
            // DESIGN_PV_DC_CAPACITY_MW into monthly { date, value } series.
            const maxMonthly = raw.max_monthly ? normalizeBenchmarkMonthlyPayload(raw.max_monthly) : [];
            const minMonthly = raw.min_monthly ? normalizeBenchmarkMonthlyPayload(raw.min_monthly) : [];
            const capacityMonthly = raw.capacity_monthly
              ? normalizeBenchmarkMonthlyPayload(raw.capacity_monthly)
              : [];
            const designCapacity =
              raw.design_pv_dc_capacity_mw != null
                ? Number(raw.design_pv_dc_capacity_mw)
                : (capacityMonthly.length > 0
                  ? capacityMonthly[capacityMonthly.length - 1].value
                  : null);
            const mostRecentMaxMw =
              raw.most_recent_max_mw != null ? Number(raw.most_recent_max_mw) : null;
            return [id, {
              maxMonthly,
              minMonthly,
              capacityMonthly,
              designCapacity,
              mostRecentMaxMw,
            }];
          } catch (apiError) {
            console.warn(
              `API fetch failed for /api/metrics/portfolio/${portfolioName}/powerblocks/${id}:`,
              apiError
            );
            return [id, {
              maxMonthly: [],
              minMonthly: [],
              capacityMonthly: [],
              designCapacity: null,
              mostRecentMaxMw: null,
            }];
          }
        })
      );

      if (!cancelled) {
        setPbBenchmarkById(Object.fromEntries(entries));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, portfolioName]);

  const pbMaxMonthly = useMemo(
    () => Object.fromEntries(
      Object.entries(pbBenchmarkById).map(([id, benchmark]) => [id, benchmark.maxMonthly ?? []])
    ),
    [pbBenchmarkById]
  );

  const pbMinMonthly = useMemo(
    () => Object.fromEntries(
      Object.entries(pbBenchmarkById).map(([id, benchmark]) => [id, benchmark.minMonthly ?? []])
    ),
    [pbBenchmarkById]
  );

  const pbCapacityMonthly = useMemo(
    () => Object.fromEntries(
      Object.entries(pbBenchmarkById).map(([id, benchmark]) => [id, benchmark.capacityMonthly ?? []])
    ),
    [pbBenchmarkById]
  );

  const designCapacityById = useMemo(
    () => Object.fromEntries(
      Object.entries(pbBenchmarkById).map(([id, benchmark]) => [id, benchmark.designCapacity ?? null])
    ),
    [pbBenchmarkById]
  );

  const maxMonthly = useMemo(
    () => pbMaxMonthly[activePB] ?? [],
    [pbMaxMonthly, activePB]
  );

  const minMonthly = useMemo(
    () => pbMinMonthly[activePB] ?? [],
    [pbMinMonthly, activePB]
  );

  const capacityMonthly = useMemo(
    () => pbCapacityMonthly[activePB] ?? [],
    [pbCapacityMonthly, activePB]
  );

  const toggleT1xfmrView = () => {
    setT1xfmrView((prev) => !prev);
  };

  // Chart plots the full selected range. Right-panel setpoint metrics +
  // Difference/Variance use only the range end month.
  const metricsMonth = useMemo(
    () => (chartRange?.end
      ? new Date(chartRange.end.getFullYear(), chartRange.end.getMonth(), 1)
      : null),
    [chartRange],
  );

  const metricsMonthRange = useMemo(() => {
    if (!metricsMonth) return null;
    return { start: metricsMonth, end: metricsMonth };
  }, [metricsMonth]);

  const maxSeries = useMemo(
    () => [
      { data: filterPointsByRange(maxMonthly, chartRange), color: SERIES_COLORS.max, label: 'MAX' },
    ],
    [maxMonthly, chartRange]
  );

  const metricsMaxPoints = useMemo(
    () => getRangeMaxPoints(maxMonthly, metricsMonthRange),
    [maxMonthly, metricsMonthRange]
  );

  // Selected-month row from ML_POWERBLOCK_BENCHMARK_MONTHLY:
  // MAX/MIN/DESIGN from Snowflake; DIFFERENCE / VARIANCE derived.
  const selectedMonthMaxMw = useMemo(
    () => valueAtMonth(maxMonthly, metricsMonth),
    [maxMonthly, metricsMonth],
  );
  const selectedMonthMinMw = useMemo(
    () => valueAtMonth(minMonthly, metricsMonth),
    [minMonthly, metricsMonth],
  );
  const designCapacity = useMemo(() => {
    const forMonth = valueAtMonth(capacityMonthly, metricsMonth);
    if (forMonth != null) return forMonth;
    return activePB && activePB !== 0 ? designCapacityById[activePB] ?? null : null;
  }, [capacityMonthly, metricsMonth, activePB, designCapacityById]);

  const capacityDifference =
    selectedMonthMaxMw != null && designCapacity != null
      ? designCapacity - selectedMonthMaxMw
      : null;
  const capacityVariance =
    capacityDifference != null && designCapacity != null && designCapacity !== 0
      ? (100 * capacityDifference) / designCapacity
      : null;

  // Tile color uses the API's global latest nonzero MAX (not the chart month range).
  const pbDifferences = useMemo(() => {
    const result = {};
    if (!data) {
      return result;
    }

    Object.keys(data).forEach((id) => {
      const benchmark = pbBenchmarkById[id];
      if (!benchmark) {
        return;
      }
      const { designCapacity, mostRecentMaxMw } = benchmark;
      if (mostRecentMaxMw != null && designCapacity != null) {
        result[id] = designCapacity - mostRecentMaxMw;
      }
    });
    return result;
  }, [data, pbBenchmarkById]);

  // Scale the gradient to the spread in the current view so tiles show relative
  // benchmarking (best = green, worst in view = red) rather than clamping at 0.6 MW.
  const differenceScaleMax = useMemo(() => {
    const values = Object.values(pbDifferences).filter((v) => v != null && v >= 0);
    if (values.length === 0) {
      return MIN_DIFFERENCE_SCALE;
    }
    const max = Math.max(...values);
    return max > 0 ? max : MIN_DIFFERENCE_SCALE;
  }, [pbDifferences]);

  const selectedStatusColor = pbDifferences[activePB] != null
    ? differenceToColor(pbDifferences[activePB], differenceScaleMax)
    : TILE_LOADING_COLOR;
  const selectedLabel = activePB && activePB !== 0 ? activePB : '--';

  // The discrete months covered by the selected range, oldest -> newest. Each
  // becomes a MAX column in the benchmarking table.
  const monthsInRange = useMemo(() => {
    if (!chartRange?.start || !chartRange?.end) return [];
    const months = [];
    const cursor = new Date(chartRange.start.getFullYear(), chartRange.start.getMonth(), 1);
    const end = new Date(chartRange.end.getFullYear(), chartRange.end.getMonth(), 1);
    while (cursor <= end) {
      const month = String(cursor.getMonth() + 1).padStart(2, '0');
      const year = cursor.getFullYear();
      months.push({ key: `${year}-${month}`, label: `${month}-${year}` });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
  }, [chartRange]);

  // Per-powerblock benchmarking figures for the table: a MAX value for each
  // month in the range, plus design capacity and Difference/Variance from the
  // selected-month MAX (POC formula).
  const tableRows = useMemo(() => {
    if (!allPBData) return [];
    return Object.entries(allPBData).map(([label, pb]) => {
      const rangePoints = getRangeMaxPoints(pbMaxMonthly[label] ?? [], chartRange);
      const maxByMonth = monthsInRange.map((month) => {
        const point = rangePoints.find((p) => {
          const key = `${p.date.getUTCFullYear()}-${String(p.date.getUTCMonth() + 1).padStart(2, '0')}`;
          return key === month.key;
        });
        return point?.value ?? null;
      });
      const values = maxByMonth.filter((v) => v != null);
      const minSetpoint = values.length ? Math.min(...values) : null;
      const maxSetpoint = values.length ? Math.max(...values) : null;
      const selectedMonthMax = valueAtMonth(pbMaxMonthly[label] ?? [], metricsMonth);
      const capacity =
        valueAtMonth(pbCapacityMonthly[label] ?? [], metricsMonth)
        ?? designCapacityById[label]
        ?? null;
      const difference =
        selectedMonthMax != null && capacity != null ? capacity - selectedMonthMax : null;
      const variance =
        difference != null && capacity != null && capacity !== 0
          ? (100 * difference) / capacity
          : null;
      return { label, pb, maxByMonth, minSetpoint, maxSetpoint, capacity, difference, variance };
    });
  }, [
    allPBData,
    monthsInRange,
    pbMaxMonthly,
    pbCapacityMonthly,
    chartRange,
    metricsMonth,
    designCapacityById,
  ]);

  useEffect(() => {
    if (t1xfmrView) {
      resetTableHost(tableHostRef, dataTableRef);
      return undefined;
    }

    const host = tableHostRef.current;
    if (!host || tableRows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped benchmark-powerblocks-table';
    host.appendChild(table);

    const columns = [
      statusColumn(
        (value) => differenceToStatusIcon(value, differenceScaleMax),
        (value) => differenceToColor(value, differenceScaleMax),
      ),
      { title: 'Powerblock', className: 'dt-right' },
      ...monthsInRange.map((month) => numericColumn(month.label)),
      numericColumn('Min Setpoint'),
      numericColumn('Max Setpoint'),
      numericColumn('Design DC Capacity'),
      numericColumn('Difference'),
      {
        title: 'Variance',
        render: (cellData, type) => {
          if (type === 'sort' || type === 'type') {
            return cellData ?? 0;
          }

          return cellData != null ? `${Number(cellData).toFixed(1)}%` : '--';
        },
      },
    ];

    const data = tableRows.map((row) => [
        row.difference ?? 0,
        row.label || '--',
        ...row.maxByMonth,
        row.minSetpoint,
        row.maxSetpoint,
        row.capacity,
        row.difference,
        row.variance,
      ]);

    dataTableRef.current = new DataTable(table, {
      data,
      columns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      autoWidth: true,
      initComplete: () => {
        dataTableRef.current?.draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableRows, t1xfmrView, monthsInRange, differenceScaleMax]);

  useEffect(() => () => {
    resetTableHost(tableHostRef, dataTableRef);
  }, []);

  if (data === undefined) {
    return null;
  }

  return (
    <div id="plantpowerblocks" className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3">
        <div className="left d-flex gap-3 align-items-center">
          <span><FontAwesomeIcon icon={faChartColumn} /> POWER PLANT CONTROLLER PERFORMANCE</span>
          <select
            className="form-select form-select-sm w-auto"
            value={selectedXfmr}
            onChange={(e) => setSelectedXfmr(e.target.value)}
          >
            {TRANSFORMER_IDS.map((id) => (
              <option key={id} value={id}>XFMR {id}</option>
            ))}
            <option value="all">All Transformers</option>
          </select>
          <MonthRangePicker range={chartRange} setRange={setChartRange} />
        </div>
        <div className="right d-flex align-items-stretch gap-2">
          <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button> <button onClick={toggleT1xfmrView}><FontAwesomeIcon icon={t1xfmrView ? faTableColumns : faTable} /></button>
        </div>
      </div>
      <div className={`body gap-5 justify-content-between pt-3 align-items-start ${ t1xfmrView ? 'd-flex' : 'd-none' }`}>
        <div className="col-8">
          <MultiLineChart
            unit="MW DC"
            tooltipUnit="MW DC"
            domain={COMBINED_DOMAIN}
            tickValues={COMBINED_TICK_VALUES}
            midlines={COMBINED_MIDLINES}
            showPoints
            wrapperClassName="combined mb-3"
            startDate={chartRange?.start}
            endDate={chartRange?.end}
            monthlyTicks={Boolean(chartRange?.start && chartRange?.end)}
            headerLeft={
              <>
                <strong style={{ color: '#000' }}>Powerblock:</strong>{' '}
                <strong style={{ color: selectedStatusColor }}>{selectedLabel}</strong>
              </>
            }
            headerRight={
              <strong style={{ color: SERIES_COLORS.max }}>MONTHLY MAX</strong>
            }
            series={maxSeries}
            onTooltip={onTooltip}
            timeZone={dateFormatter.timeZone}
          />
          <p className="small text-muted mb-2">{MAX_SERIES_CAPTION_SHORT}</p>
          <div className="powerblock-gradient-legend d-flex align-items-center gap-2 mt-3 mb-4">
            <span className="small">0</span>
            <div
              className="flex-grow-1 d-flex align-items-center justify-content-center px-2"
              style={{
                minHeight: '28px',
                borderRadius: '6px',
                background: `linear-gradient(to right, ${GRADIENT_STOPS.join(', ')})`,
              }}
            >
              <span
                className="small text-white mb-0 text-center"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.65)' }}
              >
                Difference of Design DC Capacity and selected-month MAX setpoint (MW DC)
              </span>
            </div>
            <span className="small">{differenceScaleMax.toFixed(1)}</span>
          </div>
          <ul className="powerblocks">
            {data && Object.keys(data).length > 0 &&
              Object.keys(data).map((id, index) => {
                const difference = pbDifferences[id];
                const tileColor = difference != null
                  ? differenceToColor(difference, differenceScaleMax)
                  : TILE_LOADING_COLOR;
                const isSelected = activePB === id;
                return (
                  <li
                    key={index}
                    className={isSelected ? 'selected' : ''}
                    style={
                      isSelected
                        ? { background: '#fff', border: `1px solid ${tileColor}`, color: tileColor }
                        : { background: tileColor }
                    }
                  >
                    <Link onClick={() => setActivePB(id)} className="w-100 h-100 d-flex align-items-center justify-content-center">
                      {id}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="col">
          {activePBData !== null &&
          <div className="details">
            {activePBData.status && <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>POWERBLOCK</strong> {activePB}</div>}
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>TRANSFORMER</strong> {activePBData.transformer_id != null ? `XFMR ${activePBData.transformer_id}` : '--'}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>CIRCUITS</strong> {Array.isArray(activePBData.circuits) ? (activePBData.circuits.length > 0 ? activePBData.circuits.join(', ') : '--') : (activePBData.circuits != null && activePBData.circuits !== '' ? activePBData.circuits : '--')}</div>
            {metricsMaxPoints.map((point) => {
              const month = String(point.date.getUTCMonth() + 1).padStart(2, '0');
              const year = point.date.getUTCFullYear();
              return (
                <div
                  key={point.date.getTime()}
                  className="d-flex flex-row align-items-center gap-2 justify-content-between"
                >
                  <strong>{`${month}-${year} MAX`}</strong> {fmtMwDc(point.value)}
                </div>
              );
            })}
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>MIN SETPOINT</strong> {fmtMwDc(selectedMonthMinMw)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>MAX SETPOINT</strong> {fmtMwDc(selectedMonthMaxMw)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>DESIGN DC CAPACITY</strong> {fmtMwDc(designCapacity)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>DIFFERENCE</strong> {fmtMwDc(capacityDifference)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>VARIANCE</strong> {fmtVariancePct(capacityVariance)}
            </div>
          </div>}
        </div>
      </div>
      <div className={`body pt-3 ${ t1xfmrView ? 'd-none' : 'd-block' }`}>
        {tableRows.length === 0 && (
          <p className="mb-2">No data available</p>
        )}
        <div
          ref={tableHostRef}
          className="benchmark-powerblocks-table-host"
        />
      </div>
    </div>
  );
}

export default BenchmarkPowerBlocks;
